import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  allocation,
  event as eventMock,
  id,
  Identifiable,
  List,
  milestone as milestoneMock,
  Project,
  project as projectMock,
  Resource,
  task as taskMock,
  Allocation,
  unavailability as unavailabilityMock,
  UnavailabilityReason,
} from "@planzone/dto";
import {
  useAuthentication,
  useCreateEvent,
  useCreateMilestone,
  useListsRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useTaskCreation,
  useTaskStatusesRetrieval,
  useUnavailabilityCreation,
  useRetrieveUnavailabilityReasons,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { ProjectsSelection } from "@common/projectsSelection";
import { SelectList } from "@common/selectList";
import { ArrowIcon } from "@common/assets/arrowIcon";
import classNames from "classnames";
import { calculateHours } from "./utils";
import { useWorkspaceContext } from "@scenes/workspaceContext/utils";
import { ListSelect } from "@common/listSelect";
import { TextInput } from "@common/textInput";
import { EnterIcon } from "@common/assets/enterIcon";
import { Safeguard } from "@scenes/safeguard";
import { UseCases } from "@planzone/core";
import { TypeSelector } from "./typeSelector";
import { ParticipantsPicker } from "./participantsPicker";
import { DateFields } from "./dateFields";
import { CustomFields } from "./customFields";
import { Props, ElementType } from "./types";
import styles from "./styles.scss";
import { ScreenWatcher } from "@common/screenWatcher";

const PANEL_WIDTH = 380;

function DayCreationMenu({ day, x, y, openedLeft, onClose }: Props) {
  const { t } = useTranslation();
  const menuRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState({
    x,
    y,
    visible: false,
    openedLeft: false,
    arrowTop: 20,
  });

  const [type, setType] = React.useState<ElementType>("task");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [participants, setParticipants] = React.useState<
    Identifiable<Resource>[]
  >([]);
  const [allocations, setAllocations] = React.useState<
    Identifiable<Allocation>[]
  >([]);
  const workspace = useWorkspaceContext();
  const [selectedProject, setSelectedProject] =
    React.useState<Identifiable<Project> | null>(
      workspace.type === "project" ? workspace.project : null,
    );
  const [selectedList, setSelectedList] =
    React.useState<Identifiable<List> | null>(null);
  const dayDt = DateTime.fromJSDate(day);
  const [startDate, setStartDate] = React.useState(
    dayDt.isValid ? (dayDt.toUTC().toISO() ?? "") : "",
  );
  const [endDate, setEndDate] = React.useState(
    dayDt.isValid ? (dayDt.plus({ hours: 1 }).toUTC().toISO() ?? "") : "",
  );
  const [attributes, setAttributes] = React.useState<{
    [key: string]: string;
  }>({});

  const currentProject = selectedProject || { ...projectMock, uuid: "" };

  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();

  const {
    template,
    isRequestPending: isPlanzoneCriteriaPending,
    isRequestSuccess: isPlanzoneCriteriaSuccess,
    isRequestFailure: isPlanzoneCriteriaFailure,
    retrieveSecondaryCriteriaTemplate: retrievePlanzoneCriteria,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "projectElement");

  React.useLayoutEffect(() => {
    if (!contentRef.current) return;
    const naturalHeight = contentRef.current.scrollHeight;
    const panelPadding = 32;
    const totalHeight = naturalHeight + panelPadding;
    const adjustedX = openedLeft ? x - PANEL_WIDTH : x;
    const centeredY = y - totalHeight / 2;
    const clampedY = Math.max(
      0,
      Math.min(window.innerHeight - totalHeight, centeredY),
    );
    const arrowTop = Math.max(16, Math.min(totalHeight - 16, y - clampedY));
    setPos({
      x: Math.max(0, adjustedX),
      y: clampedY,
      visible: true,
      openedLeft,
      arrowTop,
    });
  }, [type, selectedProject?.uuid, template.length]);

  const [selectedReason, setSelectedReason] =
    React.useState<Identifiable<UnavailabilityReason> | null>(null);
  const [partialUnavailability, setPartialUnavailability] =
    React.useState(true);
  const [unavailabilityResources, setUnavailabilityResources] = React.useState<
    Identifiable<Resource>[]
  >([]);

  const { isRequestPending: isTaskPending, createTask } = useTaskCreation();
  const { isRequestPending: isMilestonePending, createMilestone } =
    useCreateMilestone();
  const { isRequestPending: isEventPending, createEvent } = useCreateEvent();
  const { isRequestPending: isUnavailabilityPending, createUnavailability } =
    useUnavailabilityCreation();
  const {
    unavailabilityReasons,
    isRequestPending: isReasonsPending,
    isRequestSuccess: isReasonsSuccess,
    isRequestFailure: isReasonsFailure,
    retrieveUnavailabilityReasons,
  } = useRetrieveUnavailabilityReasons();

  const {
    statuses,
    isRequestPending: isStatusesPending,
    isRequestSuccess: isStatusesSuccess,
    isRequestFailure: isStatusesFailure,
    retrieveTaskStatuses,
  } = useTaskStatusesRetrieval(currentProject);

  const {
    lists,
    isRequestPending: isListsPending,
    isRequestSuccess: isListsSuccess,
    isRequestFailure: isListsFailure,
    retrieveLists,
  } = useListsRetrieval(currentProject, { limit: 999999999999, start: 0 });

  const isPending =
    isTaskPending ||
    isMilestonePending ||
    isEventPending ||
    isUnavailabilityPending;

  const todoStatus = statuses.find(
    ({ percentComplete, basic }) => basic && percentComplete === 0,
  );
  const taskStatus = todoStatus || statuses[0] || status;

  const listsArray: Identifiable<List>[] =
    lists && (lists as any).uuid ? [lists as Identifiable<List>] : [];

  React.useEffect(() => {
    if (account?.resource?.uuid) {
      if (allocations.length === 0) {
        setAllocations([
          { ...allocation, uuid: id, resource: account.resource },
        ]);
      }
      if (unavailabilityResources.length === 0) {
        setUnavailabilityResources([
          account.resource as Identifiable<Resource>,
        ]);
      }
    }
  }, [account?.resource?.uuid]);

  React.useEffect(() => {
    if (!selectedProject?.uuid) return;
    !isListsPending &&
      !isListsSuccess &&
      !isListsFailure &&
      retrieveLists({
        project: selectedProject,
        filter: { limit: 999999999999, start: 0 },
      });
  }, [selectedProject?.uuid]);

  React.useEffect(() => {
    if ((lists as any)?.uuid && !selectedList) {
      setSelectedList(lists as Identifiable<List>);
    }
  }, [lists]);

  React.useEffect(() => {
    !!planzone.uuid &&
      !isPlanzoneCriteriaPending &&
      !isPlanzoneCriteriaSuccess &&
      !isPlanzoneCriteriaFailure &&
      retrievePlanzoneCriteria({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "projectElement",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    if (!selectedProject?.uuid) return;
    !isStatusesPending &&
      !isStatusesSuccess &&
      !isStatusesFailure &&
      retrieveTaskStatuses(selectedProject);
  }, [selectedProject?.uuid]);

  React.useEffect(() => {
    !isReasonsPending &&
      !isReasonsSuccess &&
      !isReasonsFailure &&
      retrieveUnavailabilityReasons();
  }, []);

  React.useEffect(() => {
    if (
      isReasonsSuccess &&
      unavailabilityReasons.length > 0 &&
      !selectedReason
    ) {
      setSelectedReason(unavailabilityReasons[0]);
    }
  }, [isReasonsSuccess]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useMessage(
    ({ type: eventType }) => {
      if (
        (eventType === events.createTaskSucceeded && type === "task") ||
        (eventType === events.createMilestoneSucceeded &&
          type === "milestone") ||
        (eventType === events.createEventSucceeded && type === "event") ||
        (eventType === events.createUnavailabilitySucceeded &&
          type === "unavailability")
      ) {
        onClose();
      }
    },
    { localOnly: true },
  );

  const handleSubmit = (
    canCreateTask: boolean,
    canCreateEvent: boolean,
    canCreateMilestone: boolean,
    canCreateUnavailability: boolean,
  ) => {
    const canSubmit =
      (type === "task" && canCreateTask) ||
      (type === "event" && canCreateEvent) ||
      (type === "milestone" && canCreateMilestone) ||
      (type === "unavailability" && canCreateUnavailability);

    const needsProjectAndList = type !== "unavailability";

    if (
      !name ||
      (needsProjectAndList && (!selectedProject || !selectedList)) ||
      isPending ||
      !canSubmit
    )
      return;

    if (type === "task") {
      createTask({
        list: selectedList!,
        task: {
          ...taskMock,
          activityKeyRank: -1,
          name,
          description,
          startDate,
          endDate,
          allocations,
          attributes,
          status: taskStatus,
        },
        project: selectedProject!,
      });
    } else if (type === "milestone") {
      createMilestone({
        list: selectedList!,
        milestone: {
          ...milestoneMock,
          keyRank: -1,
          name,
          description,
          endDate: startDate,
          attributes,
        },
        project: selectedProject!,
      });
    } else if (type === "unavailability") {
      createUnavailability({
        unavailability: {
          ...unavailabilityMock,
          label: name,
          startDate,
          endDate: endDate || startDate,
          resource: unavailabilityResources[0] || account.resource,
          reason: selectedReason || unavailabilityMock.reason,
          partialUnavailability,
          nonWorkedHoursCount: partialUnavailability
            ? calculateHours(startDate, endDate)
            : 0,
          planzone,
        },
        planzone,
      });
    } else {
      createEvent({
        account,
        list: selectedList!,
        event: {
          ...eventMock,
          keyRank: -1,
          name,
          description,
          participants,
          startTime: startDate,
          endTime: endDate,
          attributes,
        },
        project: selectedProject!,
      });
    }
  };

  return (
    <ScreenWatcher
      renderChildren={() => (
        <Safeguard
          useCases={[
            UseCases.createTask,
            UseCases.createEvent,
            UseCases.createMilestone,
            UseCases.createUnavailability,
          ]}
          context={selectedProject ? { project: selectedProject } : undefined}
        >
          {({
            canCreateTask,
            canCreateEvent,
            canCreateMilestone,
            canCreateUnavailability,
          }) => (
            <div
              ref={menuRef}
              className={`${styles.panel}${pos.visible ? ` ${openedLeft ? styles.panelAnimateFromRight : styles.panelAnimateFromLeft}` : ""}`}
              style={{
                top: pos.y,
                left: pos.x,
                visibility: pos.visible ? "visible" : "hidden",
              }}
            >
              <div
                className={
                  pos.openedLeft ? styles.arrowRight : styles.arrowLeft
                }
                style={{ top: pos.arrowTop }}
              />
              <div ref={contentRef} className={styles.panelContent}>
                <input
                  className={styles.nameInput}
                  type="text"
                  value={name}
                  placeholder={
                    type === "task"
                      ? t("listsList.quickCreation.createTask")
                      : type === "event"
                        ? t("listsList.quickCreation.createEvent")
                        : type === "milestone"
                          ? t("listsList.quickCreation.createMilestone")
                          : t("listsList.quickCreation.createUnavailability")
                  }
                  onChange={(e) => setName(e.target.value)}
                  onKeyUp={(e) =>
                    e.key === "Enter" &&
                    handleSubmit(
                      canCreateTask,
                      canCreateEvent,
                      canCreateMilestone,
                      canCreateUnavailability,
                    )
                  }
                  autoFocus
                />

                <TypeSelector
                  type={type}
                  canCreateTask={canCreateTask}
                  canCreateEvent={canCreateEvent}
                  canCreateMilestone={canCreateMilestone}
                  canCreateUnavailability={canCreateUnavailability}
                  onSelect={setType}
                />

                {workspace.type !== "project" && type !== "unavailability" && (
                  <div className={styles.field}>
                    <ProjectsSelection
                      placeholder={t("calendar.creationMenu.selectProject")}
                      zone={
                        workspace.type === "zone" ? workspace.zone : undefined
                      }
                      onSelectProject={(project) => {
                        setSelectedProject(project);
                        setSelectedList(null);
                        setAttributes({});
                      }}
                    />
                  </div>
                )}

                {selectedProject && type !== "unavailability" && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      {t("calendar.creationMenu.list")}
                    </label>
                    <ListSelect
                      lists={listsArray}
                      selectedList={selectedList || undefined}
                      selectList={setSelectedList}
                    />
                  </div>
                )}

                <DateFields
                  type={type}
                  startDate={startDate}
                  endDate={endDate}
                  partialUnavailability={partialUnavailability}
                  selectedProject={selectedProject ?? undefined}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  setPartialUnavailability={setPartialUnavailability}
                />

                {type === "unavailability" && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      {t("calendar.creationMenu.reason")}
                    </label>
                    <SelectList<Identifiable<UnavailabilityReason>>
                      listClassName={styles.reasonDropdown}
                    >
                      {({ isOpen, select }) => ({
                        input: (
                          <div
                            className={classNames(styles.reasonSelect, {
                              [styles.reasonSelectOpen]: isOpen,
                            })}
                          >
                            <span>
                              {selectedReason
                                ? t(
                                    `unavailabilityReasons.${selectedReason.name}`,
                                    selectedReason.name,
                                  )
                                : t(
                                    "unavailability.creation.reasonPlaceholder",
                                  )}
                            </span>
                            <ArrowIcon
                              className={classNames(styles.reasonArrow, {
                                [styles.reasonArrowOpen]: isOpen,
                              })}
                            />
                          </div>
                        ),
                        list: (
                          <>
                            {unavailabilityReasons.map((reason) => (
                              <div
                                key={reason.uuid}
                                className={classNames(styles.reasonOption, {
                                  [styles.reasonOptionSelected]:
                                    reason.uuid === selectedReason?.uuid,
                                })}
                                onClick={() =>
                                  setSelectedReason(select(reason))
                                }
                              >
                                {t(
                                  `unavailabilityReasons.${reason.name}`,
                                  reason.name,
                                )}
                              </div>
                            ))}
                          </>
                        ),
                      })}
                    </SelectList>
                  </div>
                )}

                {type === "unavailability" && (
                  <ParticipantsPicker
                    resources={unavailabilityResources}
                    label={t("unavailability.creation.for")}
                    onAdd={(resources) =>
                      setUnavailabilityResources(resources.slice(0, 1))
                    }
                    onRemove={(resource) =>
                      setUnavailabilityResources(
                        unavailabilityResources.filter(
                          (r) => r.uuid !== resource.uuid,
                        ),
                      )
                    }
                  />
                )}

                {type === "task" && (
                  <ParticipantsPicker
                    resources={allocations.map((a) => a.resource)}
                    label={t("calendar.creationMenu.participants")}
                    onAdd={(resources) =>
                      setAllocations([
                        ...allocations,
                        ...resources.map((r) => ({
                          ...allocation,
                          uuid: id,
                          resource: r,
                        })),
                      ])
                    }
                    onRemove={(resource) =>
                      setAllocations(
                        allocations.filter(
                          (a) => a.resource.uuid !== resource.uuid,
                        ),
                      )
                    }
                  />
                )}

                {type === "event" && (
                  <ParticipantsPicker
                    resources={participants}
                    label={t("calendar.creationMenu.participants")}
                    onAdd={(resources) =>
                      setParticipants([...participants, ...resources])
                    }
                    onRemove={(resource) =>
                      setParticipants(
                        participants.filter((p) => p.uuid !== resource.uuid),
                      )
                    }
                  />
                )}

                {template.length > 0 && type !== "unavailability" && (
                  <CustomFields
                    project={currentProject}
                    template={template}
                    attributes={attributes}
                    isPending={isPending}
                    onAttributeChange={(uuid, value) =>
                      setAttributes((prev) => ({ ...prev, [uuid]: value }))
                    }
                  />
                )}

                {type !== "unavailability" && (
                  <div className={styles.field}>
                    <label className={styles.label}>
                      {t("calendar.creationMenu.description")}
                    </label>
                    <TextInput
                      value={description}
                      placeholder={t(
                        "calendar.creationMenu.descriptionPlaceholder",
                      )}
                      input={setDescription}
                      isMultiline
                    />
                  </div>
                )}

                <button
                  className={styles.submitButton}
                  onClick={() =>
                    handleSubmit(
                      canCreateTask,
                      canCreateEvent,
                      canCreateMilestone,
                      canCreateUnavailability,
                    )
                  }
                  disabled={
                    !name ||
                    (type !== "unavailability" &&
                      (!selectedProject || !selectedList)) ||
                    isPending ||
                    !(
                      (type === "task" && canCreateTask) ||
                      (type === "event" && canCreateEvent) ||
                      (type === "milestone" && canCreateMilestone) ||
                      (type === "unavailability" && canCreateUnavailability)
                    )
                  }
                >
                  <span>{t("calendar.creationMenu.submit")}</span>
                  <EnterIcon className={styles.enterIcon} />
                </button>
              </div>
            </div>
          )}
        </Safeguard>
      )}
    />
  );
}

export { DayCreationMenu };
